import React, {useState} from 'react';

import {signupText} from '../signupText';
import {Link} from 'react-router-dom';
import {internalErrorCodes} from '../../../../data/errorCodes';
import Button from '../../../../uiToolkit/Buttons/Button';
import useGoogleTagManager, {
  sendPageView,
} from '../../../../utils/googleAnalytics';
import {isProd, trackingId} from '../../../../utils/envHelper';
import styles from '../Signup.module.css';
import TextInput2 from '../../../../uiToolkit/Inputs/TextInput2/TextInput2';
import PasswordValidator, {
  isPasswordValid,
} from '../../../../uiToolkit/Inputs/PasswordValidator/PasswordValidation';
import FormattedText from '../../../../uiToolkit/FormattedText/FormattedText';
import ErrorBox2 from '../../../../uiToolkit/ErrorBox2/ErrorBox2';
import Checkbox2 from '../../../../uiToolkit/Inputs/Checkbox2/Checkbox2';

interface Props {
  onSignUp: (email: string, password: string, name: string) => void;
  errors: string[];
  isLoading: boolean;
  clearErorrs: () => void;
  isTrial: boolean;
  planName: string;
}

const SignupUI = React.memo(
  ({onSignUp, errors, isLoading, clearErorrs, isTrial, planName}: Props) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [internalErrors, setInternalErrors] = useState<string[]>([]);
    const [passwordValid, setPasswordValid] = useState(false);

    if (trackingId && isProd) {
      useGoogleTagManager(trackingId);
      sendPageView('/subscription_tier', 'Subscription page');
    }

    const onClearErrors = () => {
      setInternalErrors([]);
      clearErorrs();
    };

    const onSubmit = () => {
      const newErrors = [];
      if (
        emailFormatIsValid(email.trim()) &&
        passwordValid &&
        name.trim() !== ''
      ) {
        onSignUp(email.trim(), password, name);
      } else {
        if (!emailFormatIsValid(email.trim())) {
          newErrors.push(internalErrorCodes.INVALID_EMAIL);
        }
        if (!passwordValid) {
          newErrors.push(internalErrorCodes.INVALID_PASSWORD);
        }
        if (name.trim() === '') {
          newErrors.push(internalErrorCodes.PROVIDE_USERNAME);
        }
      }
      setInternalErrors(newErrors);
    };

    const emailFormatIsValid = (emailValue: string) => {
      return emailValue.match(
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
    };

    const onEditPassword = (value: string) => {
      setPassword(value);
      const isValid = isPasswordValid(value);
      setPasswordValid(isValid);
    };

    const buttonEnabled = () => {
      return (
        email.trim() !== '' &&
        password.trim() !== '' &&
        name.trim() !== '' &&
        name.trim().length >= 2 &&
        termsAccepted
      );
    };

    return (
      <>
        <h2 className={styles.pageTitle}>
          <FormattedText
            text={
              isTrial ? signupText.trialTitle : signupText.planTitle(planName)
            }
          />
        </h2>
        <TextInput2
          type="email"
          value={email}
          onChange={setEmail}
          placeholder={signupText.emailPlaceholder}
          label={signupText.email}
          isMandatory={true}
          uiType="minimal"
          className={styles.inputControl}
        />
        <TextInput2
          value={name}
          onChange={setName}
          label={signupText.name}
          placeholder={signupText.namePlaceholder}
          isMandatory={true}
          uiType="minimal"
          className={styles.inputControl}
        />
        <TextInput2
          type="password"
          value={password}
          onChange={onEditPassword}
          onKeyDownFunc={e => {
            if (e.key === 'Enter') {
              onSubmit();
            }
          }}
          label={signupText.password}
          placeholder={signupText.passwordPlaceholder}
          isMandatory={true}
          uiType="minimal"
          className={styles.inputControl}
        />
        <PasswordValidator value={password} className={styles.inputControl} />
        <Checkbox2
          checked={termsAccepted}
          className={styles.inputControl}
          onCheck={() => setTermsAccepted(!termsAccepted)}
          contentText={
            <span>
              {signupText.acceptTerms}{' '}
              <Link
                to={'https://tapybl.com/full-terms-and-conditions/'}
                className={styles.termsLink}
                target="_blank">
                {signupText.termsAndPrivacy}
              </Link>
            </span>
          }
        />
        <ErrorBox2
          errorCodes={[...errors, ...internalErrors]}
          clearErrors={onClearErrors}
          className={styles.errorBox}
          uiType="minimal"
        />
        <Button
          title={isTrial ? signupText.submitTrial : signupText.submit}
          onClick={onSubmit}
          disabled={!buttonEnabled()}
          isLoading={isLoading}
          uiType="signup"
          className={styles.button}
        />
      </>
    );
  },
);

export default SignupUI;
